import { useHandler } from "@redotech/react-util/hook";
import MinusIcon from "@redotech/redo-icons/minus.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef, useState } from "react";

export const QuantityIncrementDecrement = memo(
  function QuantityIncrementDecrement({
    value,
    setValue,
    max,
    min,
    title,
    subtitle,
    compact = false,
  }: {
    value: number;
    setValue(value: number): void;
    max: number;
    min: number;
    title: string;
    subtitle?: string;
    compact?: boolean;
  }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value.toString());
    const inputRef = useRef<HTMLInputElement>(null);

    const handleNumberClick = useHandler(() => {
      setIsEditing(true);
      setEditValue(value.toString());
    });

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    const handleIncrement = useHandler(() => {
      setValue(Math.min(value + 1, max));
    });

    const handleDecrement = useHandler(() => {
      setValue(Math.max(value - 1, min));
    });

    const handleEditSubmit = useHandler(() => {
      const newValue = parseInt(editValue, 10);
      if (!isNaN(newValue) && newValue >= min && newValue <= max) {
        setValue(newValue);
      }
      setIsEditing(false);
    });

    const handleBlur = useHandler((e: React.FocusEvent<HTMLInputElement>) => {
      // Small delay to ensure any pending state updates complete
      setTimeout(() => {
        handleEditSubmit();
      }, 0);
    });

    const handleEditCancel = useHandler(() => {
      setIsEditing(false);
      setEditValue(value.toString());
    });

    const handleEditKeyDown = useHandler((e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleEditSubmit();
      } else if (e.key === "Escape") {
        handleEditCancel();
      }
    });

    return (
      <Flex p={compact ? "md" : "3xl"} radius="md" w="full">
        <Flex dir="column" gap={compact ? "sm" : "md"} w="full">
          <Text fontSize={compact ? "sm" : "md"} textAlign="center">
            {title}
          </Text>
          <Flex align="center" justify="center" w="full">
            <Flex align="center" gap="md">
              {!isEditing && (
                <RedoButton
                  disabled={value <= min}
                  hierarchy="secondary"
                  IconLeading={MinusIcon}
                  onClick={handleDecrement}
                  size="md"
                  style={{
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    minWidth: "44px",
                    padding: "0",
                  }}
                />
              )}

              <Flex
                align="center"
                justify="center"
                onClick={!isEditing ? handleNumberClick : undefined}
                px="xl"
                style={{ cursor: isEditing ? "text" : "pointer" }}
              >
                {isEditing ? (
                  <Flex>
                    <RedoTextInput
                      onBlur={handleBlur}
                      onKeyDown={handleEditKeyDown}
                      ref={inputRef}
                      setValue={setEditValue}
                      size="lg"
                      value={editValue}
                    />
                  </Flex>
                ) : (
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--redo-colors-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "transparent";
                    }}
                    style={{
                      lineHeight: compact ? "48px" : "72px",
                      fontSize: compact ? "40px" : "60px",
                      color: "#FF4405",
                    }}
                    textAlign="center"
                  >
                    {value}
                  </Text>
                )}
              </Flex>
              {!isEditing && (
                <RedoButton
                  disabled={value >= max}
                  hierarchy="secondary"
                  IconLeading={PlusIcon}
                  onClick={handleIncrement}
                  size="md"
                  style={{
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    minWidth: "44px",
                    padding: 0,
                  }}
                />
              )}
            </Flex>
          </Flex>
          {subtitle && (
            <Text fontSize="xs" textAlign="center" textColor="disabled">
              {subtitle}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  },
);
